import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "minne.theme";

function readStored(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    // storage unavailable
  }
  return "system";
}

function systemDark(): boolean {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

/** CSS tokens resolved to concrete colours (SVG charts cannot always use var()). */
export interface ThemeTokens {
  chart: string[];
  foreground: string;
  mutedForeground: string;
  border: string;
  muted: string;
  popover: string;
  popoverForeground: string;
}

const FALLBACK_TOKENS: ThemeTokens = {
  chart: ["#6b7cff", "#3fb08a", "#e0a23a", "#d2609f", "#4aa3c7"],
  foreground: "#222",
  mutedForeground: "#777",
  border: "#ddd",
  muted: "#eee",
  popover: "#fff",
  popoverForeground: "#222",
};

function readTokens(): ThemeTokens {
  try {
    const cs = getComputedStyle(document.documentElement);
    const v = (name: string, fb: string) => cs.getPropertyValue(name).trim() || fb;
    return {
      chart: [1, 2, 3, 4, 5].map((i) => v(`--chart-${i}`, FALLBACK_TOKENS.chart[i - 1])),
      foreground: v("--foreground", FALLBACK_TOKENS.foreground),
      mutedForeground: v("--muted-foreground", FALLBACK_TOKENS.mutedForeground),
      border: v("--border", FALLBACK_TOKENS.border),
      muted: v("--muted", FALLBACK_TOKENS.muted),
      popover: v("--popover", FALLBACK_TOKENS.popover),
      popoverForeground: v("--popover-foreground", FALLBACK_TOKENS.popoverForeground),
    };
  } catch {
    return FALLBACK_TOKENS;
  }
}

interface ThemeCtx {
  mode: ThemeMode;
  isDark: boolean;
  tokens: ThemeTokens;
  setMode: (m: ThemeMode) => void;
  cycle: () => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStored);
  const [sys, setSys] = useState<boolean>(systemDark);

  useEffect(() => {
    let mq: MediaQueryList | null = null;
    try {
      mq = window.matchMedia("(prefers-color-scheme: dark)");
    } catch {
      return;
    }
    const on = (e: MediaQueryListEvent) => setSys(e.matches);
    mq.addEventListener("change", on);
    return () => mq?.removeEventListener("change", on);
  }, []);

  const isDark = mode === "system" ? sys : mode === "dark";

  const [tokens, setTokens] = useState<ThemeTokens>(FALLBACK_TOKENS);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", isDark);
    root.style.colorScheme = isDark ? "dark" : "light";
    setTokens(readTokens());
  }, [isDark]);

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {
      // ignore
    }
  }, []);

  const cycle = useCallback(() => {
    setMode(mode === "system" ? "light" : mode === "light" ? "dark" : "system");
  }, [mode, setMode]);

  const value = useMemo(
    () => ({ mode, isDark, tokens, setMode, cycle }),
    [mode, isDark, tokens, setMode, cycle],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useTheme outside ThemeProvider");
  return v;
}
